import { Injectable } from '@angular/core';

export interface ConflictCandidate {
  id: string;
  name: string;
  eventDate: string | Date;
  startTime?: string | null;
  endTime?: string | null;
}

export interface ConflictInfo {
  event: ConflictCandidate;
  reason: 'overlap' | 'too-close';
  gap?: number; // minutes
}

const GAP_MINUTES = 60;

function toMinutes(time?: string | null): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function dayKey(date: string | Date): string {
  return new Date(date).toISOString().slice(0, 10);
}

/**
 * Détecte les conflits entre un nouvel événement et une liste d'événements existants.
 * Conflit = même jour + chevauchement OU moins de GAP_MINUTES entre la fin de l'un et le début de l'autre.
 */
@Injectable({ providedIn: 'root' })
export class EventConflictService {
  findConflicts(
    existing: ConflictCandidate[],
    newEvent: ConflictCandidate,
  ): ConflictInfo[] {
    const newDay = dayKey(newEvent.eventDate);
    const newStart = toMinutes(newEvent.startTime);
    const newEnd = toMinutes(newEvent.endTime);

    const conflicts: ConflictInfo[] = [];

    for (const evt of existing) {
      if (dayKey(evt.eventDate) !== newDay) continue;

      const exStart = toMinutes(evt.startTime);
      const exEnd = toMinutes(evt.endTime);

      // Si on n'a pas d'heure précise → conflit par défaut (même jour)
      if (newStart == null || exStart == null) {
        conflicts.push({ event: evt, reason: 'overlap' });
        continue;
      }

      // Bornes effectives (si pas d'endTime, on suppose 1h de durée par défaut)
      const nEnd = newEnd ?? newStart + 60;
      const eEnd = exEnd ?? exStart + 60;

      // Chevauchement strict
      if (newStart < eEnd && nEnd > exStart) {
        conflicts.push({ event: evt, reason: 'overlap' });
        continue;
      }

      // Trop proche (gap < GAP_MINUTES de chaque côté)
      const gapAfter = newStart - eEnd; // new commence après existing
      const gapBefore = exStart - nEnd; // existing commence après new

      const gap = Math.min(
        gapAfter >= 0 ? gapAfter : Infinity,
        gapBefore >= 0 ? gapBefore : Infinity,
      );

      if (gap < GAP_MINUTES) {
        conflicts.push({ event: evt, reason: 'too-close', gap });
      }
    }

    return conflicts;
  }
}
